import { useState } from 'react';
import { Table, ActionIcon, Menu, Drawer, Switch } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconDotsVertical, IconEdit, IconTrash, IconCashBanknoteMove } from '@tabler/icons-react';

import { CustomTable, CustomTableMobile, useResponsive } from '@/shared';
import { getConversionPairTitle, useGetConversionPairsQuery } from '@/modules/conversion-pairs';
import { useDeletePaymentMethod } from '../hooks/use-delete-payment-method';
import { useUpdateMethodByMethodData } from '../hooks/use-update-method-by-method-data';
import { MethodActiveStatus } from './method-active-status';
import { EditPaymentMethodForm } from './payment-method-forms/edit-payment-methods-form';
import { MethodCredentials } from './method-credentials/method-credentials';
import { convertMethodsToTable } from '../utils/helpers/converter';
import { isPaymentMethodWithCustomFlow } from '../utils/helpers/payment-method-mode';

import type { TPaymentMethod, TPaymentMethodMode } from '../store/types';

type Props = {
  items: TPaymentMethod[];
  isLoading?: boolean;
};

export const PaymentMethodsTable = (props: Props) => {
  const { items } = props;
  const { isDesktop } = useResponsive();
  const [isCredentialsOpened, { open: openCredentials, close: closeCredentials }] = useDisclosure(false);
  const [currentMethodId, setCurrentMethodId] = useState<number | null>(null);
  const { data: conversionPairs } = useGetConversionPairsQuery();

  const {
    isLoading: isMethodModeUpdating,
    handleMethodUpdateByMehtodData,
  } = useUpdateMethodByMethodData();
  const { handleDeleteModal } = useDeletePaymentMethod();

  const handleOpenCredentials = (item: TPaymentMethod) => {
    setCurrentMethodId(item.id);
    openCredentials();
  };

  const openEditModal = (item: TPaymentMethod) => modals.open({
    title: `Редактирование метода с ID ${item.id}`,
    // centered: true,
    children: (
      <EditPaymentMethodForm
        paymentMethod={item}
        onClose={modals.closeAll}
      />
    ),
  });

  const getActions = (item: TPaymentMethod) => (
    <Menu position="bottom-end" shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon variant="subtle">
          <IconDotsVertical size={16} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        {item.mode === 'custom' && item.data && item.data.step1.options.length > 0 && (
          <>
            <Menu.Item
              color="teal"
              leftSection={<IconCashBanknoteMove size={14} />}
              onClick={() => handleOpenCredentials(item)}
            >
              Реквизиты
            </Menu.Item>
            <Menu.Divider />
          </>
        )}
        <Menu.Item
          leftSection={<IconEdit size={14} />}
          onClick={() => openEditModal(item)}
        >
          Редактировать
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          color="red"
          leftSection={<IconTrash size={14} />}
          onClick={() => handleDeleteModal(item)}
        >
          Удалить
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  const handleMethodModeChange = (method: TPaymentMethod) => {
    const changedMethodMode: TPaymentMethodMode = method.mode === 'custom' ? 'remote' : 'custom';
    const changedMethod = { ...method, mode: changedMethodMode };
    const isOpenEditModal = (changedMethod.mode === 'custom' && !changedMethod.data) || (changedMethod.mode === 'remote' && !changedMethod.remoteMethodId);

    if (isOpenEditModal) {
      openEditModal(changedMethod);
    } else {
      handleMethodUpdateByMehtodData({
        methodId: method.id,
        mode: changedMethodMode,
      });
    }
  };

  const getMethodConversionPair = (method: TPaymentMethod) => {
    if (!method.conversionPairId) {
      return '-';
    }

    const pair = conversionPairs?.find((item) => item.id === method.conversionPairId);

    return pair ? getConversionPairTitle(pair) : `ID ${method.conversionPairId}`;
  };

  const getMethodModeSwitch = (method: TPaymentMethod) => (
    <Switch
      checked={isPaymentMethodWithCustomFlow(method.mode)}
      disabled={method.mode === 'h2h' || isMethodModeUpdating}
      label={method.mode === 'h2h' ? 'H2H' : undefined}
      onChange={() => handleMethodModeChange(method)}
    />
  );

  const titles = [
    'ID',
    'Key',
    'Название',
    'Минимальное пополение',
    'Максимальное пополнение',
    'Валюта',
    'Торговая пара',
    'Процент пополения',
    'Комиссия с клиента (%)',
    'Активен',
    'Режим метода',
    'ID стороннего метода',
  ];

  const rows = convertMethodsToTable(items).map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>{item.id}</Table.Td>
      <Table.Td>{item.key}</Table.Td>
      <Table.Td>{item.name}</Table.Td>
      <Table.Td>{item.minRefill}</Table.Td>
      <Table.Td>{item.maxRefill}</Table.Td>
      <Table.Td>{item.currency}</Table.Td>
      <Table.Td>{getMethodConversionPair(item)}</Table.Td>
      <Table.Td>{item.refillPercent}</Table.Td>
      <Table.Td>{item.additionalPercent}</Table.Td>
      <Table.Td>
        <MethodActiveStatus method={item} />
      </Table.Td>
      <Table.Td>
        {getMethodModeSwitch(item)}
      </Table.Td>
      <Table.Td>{item.remoteMethodId ? `${item.remoteMethodId} - ${item.remoteMethodName} [${item.paymentSystemName}]` : '-'}</Table.Td>
      <Table.Td width={50}>{getActions(item)}</Table.Td>
    </Table.Tr>
  ));

  const rowsMobile = convertMethodsToTable(items).map((item) => ({
    key: item.id,
    headerActions: getActions(item),
    items: [
      { name: titles[2], value: item.name },
      { name: titles[1], value: <span style={{ fontWeight: '800' }}>{item.key}</span> },
      { name: 'Лимит', value: `${item.minRefill}-${item.maxRefill} ${item.currency}` },
      { name: titles[0], value: item.id },
      { name: titles[9], value: <MethodActiveStatus method={item} /> },
      { name: titles[3], value: item.minRefill },
      { name: titles[4], value: item.maxRefill },
      { name: titles[5], value: item.currency },
      { name: titles[6], value: getMethodConversionPair(item) },
      { name: titles[7], value: item.refillPercent },
      { name: titles[8], value: item.additionalPercent },
      {
        name: titles[10], value: (
          getMethodModeSwitch(item)
        )
      },
      { name: titles[11], value: item.remoteMethodId ? `${item.remoteMethodId} - ${item.remoteMethodName} [${item.paymentSystemName}]` : '-' },
    ],
  }));

  return (
    <>
      <Drawer
        opened={isCredentialsOpened}
        onClose={closeCredentials}
        title="Реквизиты"
        position="right"
        size="100%"
      >
        <MethodCredentials paymentMethod={items.find((item) => item.id === currentMethodId)} />
      </Drawer>

      {isDesktop
        ? (
          <CustomTable
            {...props}
            titles={titles}
            rows={rows}
            isPaginated={false}
          />
        )
        : (
          <CustomTableMobile
            rows={rowsMobile}
            headerPointsCount={3}
            isPaginated={false}
            {...props}
          />
        )
      }
    </>
  );
};
